import moment from 'moment-timezone'

import { dateTime } from '@blocktickets/shared-utils'

/**
 * Calendar YYYY-MM-DD for a react-datepicker value, preserving UTC-midnight picker semantics.
 */
export function calendarYmdFromPickerDate(date) {
  const pad = (n) => String(n).padStart(2, '0')
  const d = date instanceof Date ? date : new Date(date)
  const localDay = d.getDate()
  const utcDay = d.getUTCDate()
  const isStartOfUtcDay =
    d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0
  const useUtcDate = isStartOfUtcDay && localDay !== utcDay
  const y = useUtcDate ? d.getUTCFullYear() : d.getFullYear()
  const mo = useUtcDate ? d.getUTCMonth() : d.getMonth()
  const day = useUtcDate ? d.getUTCDate() : d.getDate()
  return `${y}-${pad(mo + 1)}-${pad(day)}`
}

export function isDailySettlementDateRangeInvalid(fromDate, toDate) {
  if (!fromDate || !toDate) return false
  return calendarYmdFromPickerDate(toDate) < calendarYmdFromPickerDate(fromDate)
}

export function isDailySettlementCustomDateRange(fromDate, toDate) {
  if (!fromDate || !toDate) return false
  return (
    calendarYmdFromPickerDate(fromDate) !== calendarYmdFromPickerDate(toDate)
  )
}

/**
 * Builds UTC bounds and getPaymentListingsV4 date_paid options.
 * Single-day (no end date, or end equals start) uses datePaidOn for refund-inclusive behavior.
 */
export function buildDailySettlementQueryParams(fromDate, toDate, tz) {
  const startYmd = calendarYmdFromPickerDate(fromDate)
  const useRange = toDate && isDailySettlementCustomDateRange(fromDate, toDate)

  if (useRange) {
    const endYmd = calendarYmdFromPickerDate(toDate)
    const startLocalIso = `${startYmd}T00:00:00.000`
    const endLocalIso = `${endYmd}T23:59:59.999`

    return {
      fromUtcIso: dateTime.localToUtc(startLocalIso, tz).toISOString(),
      toUtcIso: dateTime.localToUtc(endLocalIso, tz).toISOString(),
      options: {
        dateField: 'date_paid',
        datePaidFrom: startYmd,
        datePaidTo: endYmd,
      },
    }
  }

  const ymdStr = startYmd
  const startLocalIso = `${ymdStr}T00:00:00.000`
  const nextDayStr = moment
    .utc(`${ymdStr}T00:00:00.000`)
    .add(1, 'day')
    .format('YYYY-MM-DD')
  const endExclusiveLocalIso = `${nextDayStr}T00:00:00.000`

  return {
    fromUtcIso: dateTime.localToUtc(startLocalIso, tz).toISOString(),
    toUtcIso: dateTime.localToUtc(endExclusiveLocalIso, tz).toISOString(),
    options: {
      dateField: 'date_paid',
      datePaidOn: ymdStr,
    },
  }
}

export function formatDailySettlementReportDateLabel(fromDate, toDate) {
  if (!fromDate) return ''
  const startLabel = moment(fromDate).format('MMM D, YYYY')
  if (
    !toDate ||
    calendarYmdFromPickerDate(fromDate) === calendarYmdFromPickerDate(toDate)
  ) {
    return startLabel
  }
  return `${startLabel} - ${moment(toDate).format('MMM D, YYYY')}`
}
